import * as Sentry from '@sentry/nextjs';
import { redactAuthLinks } from './sentry-redaction';

type SentryContextUser = {
  id: string;
  email?: string | null;
  tenantId?: string;
};

/**
 * Thin wrapper over @sentry/nextjs so call sites stay stable. Every event is
 * tagged with the tenant, and NEVER carries an email address — admin emails
 * are congregation data and stay out of third-party telemetry (PII rule).
 */
export function setSentryUserContext(user: SentryContextUser) {
  Sentry.setUser({ id: user.id });
  if (user.tenantId) {
    Sentry.setTag('tenantId', user.tenantId);
  }
}

/**
 * Report a caught error from a route handler or deferred background task.
 * Same PII rule as above: never put an email address in the error.
 */
export function captureSentryException(error: unknown) {
  Sentry.captureException(error);
}

/** Next.js onRequestError hook (src/instrumentation.ts) → Sentry. */
export function captureSentryRequestError(
  ...args: Parameters<typeof Sentry.captureRequestError>
) {
  Sentry.captureRequestError(...redactAuthLinks(args));
}

/**
 * DSN-less environments (dev, tests, CI) skip init entirely so nothing is
 * ever buffered or sent; prod gets the DSN from Vercel env.
 */
export function initSentry(options: { dsn?: string } & Parameters<typeof Sentry.init>[0]) {
  if (!options.dsn) return;
  Sentry.init({
    ...options,
    beforeSend: async (event, hint) => {
      const safeEvent = redactAuthLinks(event);
      const result = options.beforeSend ? await options.beforeSend(safeEvent, hint) : safeEvent;
      return result === null ? null : redactAuthLinks(result, event);
    },
    beforeSendTransaction: async (event, hint) => {
      const safeEvent = redactAuthLinks(event);
      const result = options.beforeSendTransaction
        ? await options.beforeSendTransaction(safeEvent, hint)
        : safeEvent;
      return result === null ? null : redactAuthLinks(result, event);
    },
  });
}
